/**
 * RTK Query API slice for Announcements feature
 *
 * Provides API endpoints for managing announcements, configuration,
 * and fetching public announcement widget data.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import type {
  Announcement,
  AnnouncementFormData,
  AnnouncementConfig,
  AnnouncementConfigUpdate,
  PublicAnnouncementWidget,
} from '../types/announcements';

export const announcementsApi = createApi({
  reducerPath: 'announcementsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Announcements', 'AnnouncementConfig', 'PublicAnnouncementWidget'],
  endpoints: builder => ({
    /**
     * Get all announcements for a studio
     *
     * GET /api/v1/studios/{studioId}/announcements/
     */
    getAnnouncements: builder.query<Announcement[], string>({
      query: studioId => `/studios/${studioId}/announcements/`,
      providesTags: (result, error, studioId) => [
        { type: 'Announcements', id: studioId },
        ...(result || []).map(({ id }) => ({
          type: 'Announcements' as const,
          id,
        })),
      ],
    }),

    /**
     * Create a new announcement
     *
     * POST /api/v1/studios/{studioId}/announcements/
     */
    createAnnouncement: builder.mutation<
      Announcement,
      { studioId: string; data: AnnouncementFormData }
    >({
      query: ({ studioId, data }) => ({
        url: `/studios/${studioId}/announcements/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'Announcements', id: studioId },
        // Also invalidate public widget when announcements change
        { type: 'PublicAnnouncementWidget', id: studioId },
      ],
    }),

    /**
     * Update an announcement
     *
     * PATCH /api/v1/studios/{studioId}/announcements/{announcementId}/
     */
    updateAnnouncement: builder.mutation<
      Announcement,
      {
        studioId: string;
        announcementId: string;
        data: Partial<AnnouncementFormData>;
      }
    >({
      query: ({ studioId, announcementId, data }) => ({
        url: `/studios/${studioId}/announcements/${announcementId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { studioId, announcementId }) => [
        { type: 'Announcements', id: studioId },
        { type: 'Announcements', id: announcementId },
        { type: 'PublicAnnouncementWidget', id: studioId },
      ],
    }),

    /**
     * Delete an announcement
     *
     * DELETE /api/v1/studios/{studioId}/announcements/{announcementId}/
     */
    deleteAnnouncement: builder.mutation<
      void,
      { studioId: string; announcementId: string }
    >({
      query: ({ studioId, announcementId }) => ({
        url: `/studios/${studioId}/announcements/${announcementId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { studioId, announcementId }) => [
        { type: 'Announcements', id: studioId },
        { type: 'Announcements', id: announcementId },
        { type: 'PublicAnnouncementWidget', id: studioId },
      ],
    }),

    /**
     * Bulk reorder a studio's announcements
     *
     * POST /api/v1/studios/{studioId}/announcements/reorder/
     * Body: { ids: ["<uuid1>", "<uuid2>", ...] }
     *
     * Server writes display_order = array index. Applies an optimistic
     * update so the list visually reorders the moment the user releases a
     * drag; on failure the previous order is restored and the cache is
     * invalidated so the next refresh re-syncs from the server.
     */
    reorderAnnouncements: builder.mutation<
      Announcement[],
      { studioId: string; ids: string[] }
    >({
      query: ({ studioId, ids }) => ({
        url: `/studios/${studioId}/announcements/reorder/`,
        method: 'POST',
        body: { ids },
      }),
      async onQueryStarted({ studioId, ids }, { dispatch, queryFulfilled }) {
        // Reorder the existing cache entry in place so the UI updates
        // immediately. Each row's display_order is set to its new index so
        // any downstream sort by display_order stays consistent.
        const patchResult = dispatch(
          announcementsApi.util.updateQueryData(
            'getAnnouncements',
            studioId,
            draft => {
              const byId = new Map(draft.map(a => [a.id, a]));
              const reordered = ids
                .map((id, index) => {
                  const row = byId.get(id);
                  return row ? { ...row, display_order: index } : null;
                })
                .filter((row): row is Announcement => row !== null);
              // Preserve any announcements not present in the ids array
              // (shouldn't normally happen, but defends against stale state).
              const seen = new Set(ids);
              const trailing = draft.filter(a => !seen.has(a.id));
              draft.splice(0, draft.length, ...reordered, ...trailing);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          // Server rejected (network blip, validation, IDOR). Roll back the
          // optimistic patch so the UI snaps back to the previous order.
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { studioId }) => [
        // After a successful reorder, refresh the public widget too so it
        // reflects the new order without a manual reload.
        { type: 'PublicAnnouncementWidget', id: studioId },
      ],
    }),

    /**
     * Get or create announcement configuration for a studio
     *
     * GET /api/v1/studios/{studioId}/announcements/config/
     */
    getAnnouncementConfig: builder.query<AnnouncementConfig, string>({
      query: studioId => `/studios/${studioId}/announcements/config/`,
      providesTags: (result, error, studioId) => [
        { type: 'AnnouncementConfig', id: studioId },
      ],
    }),

    /**
     * Update announcement configuration
     *
     * PATCH /api/v1/studios/{studioId}/announcements/config/
     */
    updateAnnouncementConfig: builder.mutation<
      AnnouncementConfig,
      { studioId: string; data: AnnouncementConfigUpdate }
    >({
      query: ({ studioId, data }) => ({
        url: `/studios/${studioId}/announcements/config/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'AnnouncementConfig', id: studioId },
        // Also invalidate public widget when config changes
        { type: 'PublicAnnouncementWidget', id: studioId },
      ],
    }),

    /**
     * Get public announcement widget data (no authentication required)
     *
     * GET /api/v1/public/announcements/{studioSlugOrId}/
     */
    getPublicAnnouncementWidget: builder.query<
      PublicAnnouncementWidget,
      string
    >({
      query: studioIdentifier => `/public/announcements/${studioIdentifier}/`,
      providesTags: (result, error, studioIdentifier) => [
        { type: 'PublicAnnouncementWidget', id: studioIdentifier },
      ],
    }),
  }),
});

export const {
  useGetAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useDeleteAnnouncementMutation,
  useReorderAnnouncementsMutation,
  useGetAnnouncementConfigQuery,
  useUpdateAnnouncementConfigMutation,
  useGetPublicAnnouncementWidgetQuery,
} = announcementsApi;
